/**
 * 有効期間 (validFrom / validTo) の Controller + Input ブロックを共通化した component。
 *
 * PlanMasterBgsSection / SuppressionPartnerBgsSection / SuppressionContractsSection /
 * PlanMasterPlantsSection / PlanMasterCodesSection / OwnerMembersSection (BG マスタ
 * 構成事業者行) / BgPlantMembershipSection で繰り返すコピペを排除する。
 *
 * i18n key は `${i18nNs}.form.validFromLabel` / `${i18nNs}.form.validToLabel` を踏襲。
 * 日付入力 UI (Flatpickr 相当 compact calendar popup) は `CompactCivilDateField`
 * を経由する (トップレベル `TopLevelValidPeriodFields` と実装を共有、 2026-08-05
 * リファクタで raw `<Input type="date">` から移行)。
 */
import { type Control, Controller, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, FieldRoot, HStack } from '@enechain/ecloud-designsystem'

import { CompactCivilDateField } from '~/components/common/CompactCivilDateField'
import { FieldErrorText } from '~/components/common/Field'
import type { CivilDate } from '~/models/civilDate'
import { SubFormLabel } from '~/pages/AdminOcctoMaster/tabs/_common/components/SubFormLabel'

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any, FieldValues>
  /**
   * フィールド名のベースパス。
   * validFrom は `${basePath}.validFrom`、 validTo は `${basePath}.validTo` にバインドされる。
   */
  basePath: string
  /**
   * i18n ページキー namespace (= 'page:${i18nNs}.form.validFromLabel' 等)。
   * 呼び出し側の i18nNs と同一の値を渡す。
   */
  i18nNs: string
  /** flex balance 用の末尾スペーサー幅。デフォルト "64px" */
  spacerWidth?: string
  /**
   * validTo クリア時に `undefined` ではなく `null` を書き込むか (既定 false)。
   *
   * RHF は `useFieldArray` 内のネスト field を `undefined` にする書き込み
   * (`field.onChange(undefined)` / `setValue(undefined)` / `resetField` /
   * `update({...,validTo:undefined})`) を**すべて無視**し、値が `reset()` で入った
   * まま残る (= 編集モーダルで行の適用終了日をクリアできない不具合。top-level の
   * validTo は影響を受けない)。`null` (defined 値) は確実に反映されるため、
   * field-array 行の validTo をクリアする呼び出し側はこれを true にし、対応する
   * schema 側で `null` を許容 (`.nullish().transform(v => v ?? undefined)`) する。
   */
  clearAsNull?: boolean
  /**
   * true のとき、 validFrom / validTo の両入力欄を readonly (操作不可) にする。
   * BG マスタの代表事業者行 (構成事業者リスト先頭行) は AC-OMB-021 により
   * BG 本体の適用期間へ自動同期される readonly 欄になる (issue #6038)。
   * 既定は false (= 従来通り編集可能)。
   */
  disabled?: boolean
}

export const PeriodFields = ({
  control,
  basePath,
  i18nNs,
  spacerWidth = '64px',
  clearAsNull = false,
  disabled = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  // htmlFor / id の一意化 (同一 form 内で複数行が並ぶため、 basePath を含めて
  // 衝突しないようにする)。 basePath の '.' はそのまま id 文字として扱う。
  const idFrom = `${basePath}.validFrom`
  const idTo = `${basePath}.validTo`

  return (
    <HStack align="flex-end" gap={3}>
      <Box flex={1}>
        <Controller
          control={control}
          name={`${basePath}.validFrom`}
          render={({ field: f, fieldState }) => (
            <FieldRoot invalid={fieldState.error !== undefined}>
              <SubFormLabel htmlFor={idFrom}>
                {t(`page:${i18nNs}.form.validFromLabel`)}
              </SubFormLabel>
              <CompactCivilDateField
                disabled={disabled}
                id={idFrom}
                onChange={(d) => {
                  if (d !== undefined) f.onChange(d)
                }}
                value={f.value as CivilDate | undefined}
              />
              {fieldState.error !== undefined && (
                <FieldErrorText>{fieldState.error.message}</FieldErrorText>
              )}
            </FieldRoot>
          )}
        />
      </Box>
      <Box flex={1}>
        <Controller
          control={control}
          name={`${basePath}.validTo`}
          render={({ field: f, fieldState }) => (
            <FieldRoot invalid={fieldState.error !== undefined}>
              <SubFormLabel htmlFor={idTo}>
                {t(`page:${i18nNs}.form.validToLabel`)}
              </SubFormLabel>
              <CompactCivilDateField
                disabled={disabled}
                hasClear
                id={idTo}
                onChange={(d) => {
                  // クリア (d===undefined) は clearAsNull なら null を書く
                  // (Props の clearAsNull JSDoc 参照: RHF は field-array 内の
                  // undefined 書き込みを無視するため)。
                  if (d === undefined && clearAsNull) {
                    f.onChange(null)
                  } else {
                    f.onChange(d)
                  }
                }}
                value={f.value as CivilDate | undefined}
              />
              {fieldState.error !== undefined && (
                <FieldErrorText>{fieldState.error.message}</FieldErrorText>
              )}
            </FieldRoot>
          )}
        />
      </Box>
      {/* flex balance */}
      <Box flexShrink={0} width={spacerWidth} />
    </HStack>
  )
}
